/**
 * Style A — BBC/Discovery lower-third gradient
 * Horizontal strip: dark left → transparent right. No box, no border.
 */
import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import { loadFont as loadDevanagari } from "@remotion/google-fonts/NotoSansDevanagari";

const { fontFamily } = loadFont("normal", { weights: ["400", "600", "700", "800"], subsets: ["latin"] });
const { fontFamily: devFont } = loadDevanagari("normal", { weights: ["400", "700"], subsets: ["devanagari"] });

const GOLD = "#D4A853";

const LowerThird: React.FC<{
  name: string; nameHindi?: string; role: string; period?: string;
  frame: number; fps: number; durationInFrames: number; bottom?: number;
}> = ({ name, nameHindi, role, period, frame, fps, durationInFrames, bottom = 60 }) => {
  const fadeIn = interpolate(frame, [0, Math.round(0.4 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const slideX = interpolate(frame, [0, Math.round(0.4 * fps)], [-24, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.5 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  return (
    <div style={{
      position: "absolute", bottom, left: 0,
      width: 560,
      opacity: fadeIn * fadeOut,
      transform: `translateX(${slideX}px)`,
      background: "linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.72) 65%, transparent 100%)",
      padding: "14px 80px 14px 28px",
    }}>
      {/* Gold accent line on left edge */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0,
        width: 3, background: GOLD,
      }} />
      <div style={{ color: "#fff", fontSize: 19, fontWeight: 800, fontFamily, lineHeight: 1.15 }}>
        {name}
      </div>
      {nameHindi && (
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 400, fontFamily: devFont, marginTop: 2 }}>
          {nameHindi}
        </div>
      )}
      <div style={{ color: GOLD, fontSize: 12, fontWeight: 700, fontFamily, marginTop: 5, textTransform: "uppercase", letterSpacing: "1.2px" }}>
        {role}
      </div>
      {period && (
        <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 10, fontFamily, marginTop: 2 }}>
          {period}
        </div>
      )}
    </div>
  );
};

const YearLowerThird: React.FC<{
  display: string; displayLatin?: string; context?: string;
  frame: number; fps: number; durationInFrames: number;
}> = ({ display, displayLatin, context, frame, fps, durationInFrames }) => {
  const fadeIn = interpolate(frame, [0, Math.round(0.4 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });
  const slideX = interpolate(frame, [0, Math.round(0.4 * fps)], [-24, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.5 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const isDevanagari = /[ऀ-ॿ]/.test(display);

  return (
    <div style={{
      position: "absolute", top: 28, left: 0,
      width: 480,
      opacity: fadeIn * fadeOut,
      transform: `translateX(${slideX}px)`,
      background: "linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.72) 65%, transparent 100%)",
      padding: "12px 70px 12px 28px",
    }}>
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0,
        width: 3, background: GOLD,
      }} />
      <div style={{
        color: GOLD, fontSize: isDevanagari ? 22 : 28, fontWeight: 800,
        fontFamily: isDevanagari ? "system-ui" : fontFamily, lineHeight: 1,
      }}>
        {display}
      </div>
      {displayLatin && (
        <div style={{ color: "rgba(212,168,83,0.6)", fontSize: 11, fontWeight: 600, fontFamily, marginTop: 3 }}>
          {displayLatin}
        </div>
      )}
      {context && (
        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, fontWeight: 400, fontFamily, marginTop: 6, lineHeight: 1.4, maxWidth: 320 }}>
          {context}
        </div>
      )}
    </div>
  );
};

export const StyleA_Preview: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <LowerThird
        name="Nara and Narayana" nameHindi="नर और नारायण"
        role="Sages performing penance at Badri Ashram" period="Mythological Era"
        frame={frame} fps={fps} durationInFrames={durationInFrames} bottom={60}
      />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
